import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { SpinoffPlatformOrgContractService } from '../api-generated/spinoff-platform-orgs';
import type { OrgInvitationReceivedDto } from '../api-generated/models';
import { SpinoffOrgsStore } from './spinoff-orgs.service';

/**
 * "내가 받은 초대 목록"을 공유하는 signal 스토어.
 *
 * org 스위처(`SpinoffOrgSwitcherComponent`)의 뱃지 카운트와 sbe-sms
 * `/my-org-invitations`(`OrgInvitationsComponent`)가 각자 `listMyInvitations()`를 독립 호출해
 * 서로 다른 로컬 상태를 들고 있으면, 한쪽에서 수락/거절해도 다른 쪽(특히 스위처 뱃지)이 그
 * 사실을 몰라 새로고침 전까지 stale 상태로 남는다 — "내 단체 목록"의 stale 동기화와 같은 종류다. 이 스토어가 목록·`accept()`/`reject()`를 캡슐화하고, `accept()` 성공 시 내부에서
 * `SpinoffOrgsStore.refresh()`를 호출해 "내 단체 목록" 쪽도 함께 갱신한다(수락은 새 멤버십을
 * 만드는 동작이라 두 목록 모두에 영향을 준다).
 */
@Injectable({ providedIn: 'root' })
export class SpinoffOrgInvitationsStore {
  private readonly orgsService = inject(SpinoffPlatformOrgContractService);
  private readonly orgsStore = inject(SpinoffOrgsStore);

  private readonly _invitations = signal<OrgInvitationReceivedDto[]>([]);
  private readonly _loading = signal(false);
  private refreshPromise: Promise<void> | null = null;
  // fetch 응답을 적용해도 되는지 판별하는 세대 번호 — accept()/reject()의 로컬 목록 변경을
  // 더 오래된 in-flight refresh()가 나중에 덮어쓰지 못하게 한다(SpinoffOrgsStore와 동일
  // 패턴, Gemini 리뷰 지적, 2026-08-09).
  private generation = 0;

  readonly invitations = this._invitations.asReadonly();
  readonly pendingCount = computed(() => this._invitations().length);
  readonly loading = this._loading.asReadonly();

  /**
   * `SpinoffOrgsStore.refresh()`와 동일하게 in-flight promise를 캐시해 중복 호출을 막는다.
   * 이 요청이 나가 있는 동안 `accept()`/`reject()`로 로컬 목록이 바뀌면, 나중에 이 요청이
   * 완료돼도 그 변경을 덮어쓰지 않는다(`generation` 확인).
   */
  async refresh(): Promise<void> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }
    const generation = this.generation;
    this._loading.set(true);
    const promise = firstValueFrom(this.orgsService.spinoffOrgMembersControllerListMyInvitations())
      .then((res) => {
        if (generation === this.generation) {
          this._invitations.set(res.items);
        }
      })
      .finally(() => {
        this._loading.set(false);
        if (this.refreshPromise === promise) {
          this.refreshPromise = null;
        }
      });
    this.refreshPromise = promise;
    return promise;
  }

  /**
   * 초대를 수락한다. 목록에서 즉시 제거하고, 새로 생기는 멤버십을 "내 단체 목록"에 반영하기
   * 위해 `SpinoffOrgsStore.refresh()`를 호출한다. accept 응답(`{ id, orgId, status }`)에는
   * "내 단체 목록" 항목을 만들 단체 이름·내 역할·팀이 없어 로컬에서 낙관적으로 구성할 수 없다.
   *
   * `SpinoffOrgsStore.invalidate()`를 먼저 호출한다 — 안 그러면 이 accept POST가 완료되기
   * 전부터 이미 in-flight였던 낡은 `orgsStore.refresh()`를 그대로 재사용해, 방금 생긴
   * 멤버십이 반영 안 된 응답으로 끝날 수 있다(Gemini 리뷰 지적, 2026-08-09).
   *
   * 이어지는 `orgsStore.refresh()`는 accept 자체와 별개의 best-effort 부수효과라 실패해도
   * 삼킨다 — 여기서 throw하면 accept 자체는 서버에서 이미 성공했는데(초대도 로컬 목록에서
   * 이미 제거됨) 호출부(`OrgInvitationsComponent`)가 "수락 실패"로 오인해 에러 메시지를
   * 띄우게 된다(리뷰 지적, 2026-08-09). 실패 시 "내 단체 목록"은 다음 refresh() 전까지만
   * stale로 남는다.
   */
  async accept(invitationId: string): Promise<void> {
    await firstValueFrom(this.orgsService.spinoffOrgMembersControllerAcceptInvitation(invitationId));
    this.generation++;
    this._invitations.update((cur) => cur.filter((inv) => inv.id !== invitationId));
    this.orgsStore.invalidate();
    await this.orgsStore.refresh().catch(() => undefined);
  }

  /**
   * 초대를 거절한다. 멤버십 변화가 없으므로 "내 단체 목록"에는 영향 없다. in-flight
   * `refresh()`가 있다면 세대 번호를 올려, 그 응답이 나중에 도착해도 이 로컬 제거를
   * 덮어쓰지 못하게 한다.
   */
  async reject(invitationId: string): Promise<void> {
    await firstValueFrom(this.orgsService.spinoffOrgMembersControllerRejectInvitation(invitationId));
    this.generation++;
    this._invitations.update((cur) => cur.filter((inv) => inv.id !== invitationId));
  }
}
